import React, { useMemo, useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Bed, Bath, Square, ArrowRight, Filter } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/PageHeader';
import { houses, Property } from '../data/properties';
const statusFilters = ['All', 'Selling', 'Sold Out', 'Sale Closed'] as const;
function StatusPill({
  status
}: {
  status: Property['status'];
}) {
  const map = {
    Selling: 'bg-emerald-500 text-white',
    'Sold Out': 'bg-ink text-white',
    'Sale Closed': 'bg-ink/70 text-white'
  } as const;
  return <span className={`text-[10px] font-bold tracking-[0.2em] uppercase px-3 py-1.5 rounded-full ${map[status]}`} data-id="element-1143">
      {status === 'Selling' && <span className="inline-block w-1.5 h-1.5 rounded-full bg-white mr-2 animate-pulse" data-id="element-1144" />}
      {status}
    </span>;
}
export function Houses() {
  const [filter, setFilter] = useState<(typeof statusFilters)[number]>('All');
  const filtered = useMemo(() => filter === 'All' ? houses : houses.filter(h => h.status === filter), [filter]);
  const sellingCount = houses.filter(h => h.status === 'Selling').length;
  return <div className="pt-28 pb-24" data-id="element-1145">
      <PageHeader eyebrow="Properties" title="Houses & Apartments" subtitle={`Premium homes built and delivered by Zylus across Lagos and Abuja. ${sellingCount} estates currently selling.`} breadcrumb={[{
      label: 'Home',
      href: '/'
    }, {
      label: 'Properties'
    }, {
      label: 'Houses'
    }]} data-id="element-1146" />

      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 md:px-12 mt-12" data-id="element-1147">
        {/* Filter pills */}
        <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 hide-scrollbar" data-id="element-1148">
          <Filter className="w-4 h-4 text-ink/40 shrink-0" data-id="element-1149" />
          {statusFilters.map(s => {
          const count = s === 'All' ? houses.length : houses.filter(h => h.status === s).length;
          return <button key={s} onClick={() => setFilter(s)} className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest whitespace-nowrap transition-all flex items-center gap-2 ${filter === s ? 'bg-ink text-white shadow-md' : 'bg-white text-ink/70 border border-ink/10 hover:border-ink/30'}`} data-id="element-1150">
                {s}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${filter === s ? 'bg-gold text-ink' : 'bg-ink/5 text-ink/50'}`} data-id="element-1151">
                  {count}
                </span>
              </button>;
        })}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1152">
          <AnimatePresence mode="popLayout" data-id="element-1153">
            {filtered.map((h, i) => <motion.div key={h.id} layout initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.9
          }} transition={{
            delay: i % 3 * 0.08,
            duration: 0.5
          }} whileHover={{
            y: -6
          }} data-id="element-1154">
                <Link to={`/houses/${h.slug}`} className="group block bg-white rounded-2xl overflow-hidden border border-ink/5 shadow-sm hover:shadow-xl hover:border-gold/30 transition-all duration-500" data-id="element-1155">
                  <div className="relative aspect-[4/3] overflow-hidden bg-ink/5" data-id="element-1156">
                    <img src={h.hero} alt={h.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" data-id="element-1157" />
                    {h.status !== 'Selling' && <div className="absolute inset-0 bg-ink/30 backdrop-blur-[1px]" data-id="element-1158" />}
                    <div className="absolute top-3 left-3" data-id="element-1159">
                      <StatusPill status={h.status} data-id="element-1160" />
                    </div>
                    <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-ink" data-id="element-1161">
                      {h.type}
                    </div>
                  </div>

                  <div className="p-6" data-id="element-1162">
                    <h3 className="font-serif text-xl text-ink mb-2 group-hover:text-gold-dark transition-colors" data-id="element-1163">
                      {h.name}
                    </h3>
                    <div className="flex items-start gap-1.5 text-xs text-ink/60 mb-4" data-id="element-1164">
                      <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gold-dark" data-id="element-1165" />
                      <span data-id="element-1166">{h.location}</span>
                    </div>

                    <div className="flex items-center gap-3 text-xs text-ink/65 mb-5 pb-5 border-b border-ink/5" data-id="element-1167">
                      {h.beds && <span className="flex items-center gap-1" data-id="element-1168">
                          <Bed className="w-3.5 h-3.5" data-id="element-1169" /> {h.beds}
                        </span>}
                      {h.baths && <span className="flex items-center gap-1" data-id="element-1170">
                          <Bath className="w-3.5 h-3.5" data-id="element-1171" /> {h.baths}
                        </span>}
                      {h.sqft && <span className="flex items-center gap-1" data-id="element-1172">
                          <Square className="w-3.5 h-3.5" data-id="element-1173" /> {h.sqft}
                        </span>}
                    </div>

                    <div className="flex items-center justify-between" data-id="element-1174">
                      <div data-id="element-1175">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-ink/40" data-id="element-1176">
                          {h.status === 'Selling' ? 'From' : 'Status'}
                        </div>
                        <div className="font-serif text-lg text-ink" data-id="element-1177">
                          {h.priceFrom}
                        </div>
                      </div>
                      <div className="w-9 h-9 rounded-full bg-bone group-hover:bg-gold flex items-center justify-center transition-colors" data-id="element-1178">
                        <ArrowRight className="w-4 h-4 text-ink group-hover:translate-x-0.5 transition-transform" data-id="element-1179" />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>)}
          </AnimatePresence>
        </div>

        {filtered.length === 0 && <div className="text-center py-16 text-ink/50" data-id="element-1180">
            No properties match this filter.
          </div>}
      </div>
    </div>;
}